/**
 * Dashboard Page Component
 * Shows overview of client data including issues and stakeholders
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { ClipboardList, AlertCircle, Circle, Users } from 'lucide-react';
import DailyBriefingSummaryCard from '../components/daily-briefing/DailyBriefingSummaryCard';
import './Dashboard.css';

interface DashboardData {
  client: {
    id: string;
    name: string;
    description: string | null;
  };
  issue_stats: {
    total: number;
    high_priority: number;
    medium_priority: number;
    low_priority: number;
  };
  recent_issues: Array<{
    id: string;
    title: string;
    priority: string;
    created_at: string;
  }>;
  stakeholder_count: number;
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getDashboard();
      setData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>❌ {error}</p>
        <button onClick={loadDashboard}>Retry</button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="dashboard-subtitle">Welcome to your HEAT portal</p>
      </div>

      {/* Daily Briefing Summary Card */}
      <div className="dashboard-section" data-tour="daily-briefing-card">
        <DailyBriefingSummaryCard />
      </div>

      {/* Stats cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <ClipboardList className="stat-icon" size={32} strokeWidth={1.5} />
          <div className="stat-content">
            <div className="stat-value">{data.issue_stats.total}</div>
            <div className="stat-label">Total Issues</div>
          </div>
        </div>

        <div className="stat-card high-priority">
          <Circle className="stat-icon" size={32} fill="#ef4444" stroke="#ef4444" />
          <div className="stat-content">
            <div className="stat-value">{data.issue_stats.high_priority}</div>
            <div className="stat-label">High Priority</div>
          </div>
        </div>

        <div className="stat-card medium-priority">
          <Circle className="stat-icon" size={32} fill="#f59e0b" stroke="#f59e0b" />
          <div className="stat-content">
            <div className="stat-value">{data.issue_stats.medium_priority}</div>
            <div className="stat-label">Medium Priority</div>
          </div>
        </div>

        <div className="stat-card">
          <Users className="stat-icon" size={32} strokeWidth={1.5} />
          <div className="stat-content">
            <div className="stat-value">{data.stakeholder_count}</div>
            <div className="stat-label">Stakeholders</div>
          </div>
        </div>
      </div>

      {/* Recent issues */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>Recent Issues</h2>
          <Link to="/issues" className="view-all-link">View all →</Link>
        </div>
        
        {data.recent_issues.length === 0 ? (
          <div className="empty-state">
            <p>No issues found</p>
          </div>
        ) : (
          <div className="issues-list">
            {data.recent_issues.map((issue) => (
              <div key={issue.id} className="issue-item">
                <div className="issue-content">
                  <div className="issue-title">{issue.title}</div>
                  <div className="issue-meta">
                    <span className={`priority-badge priority-${issue.priority}`}>
                      {issue.priority}
                    </span>
                    <span className="issue-date">
                      {new Date(issue.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
